/**
 * Klient for systemadminen (#178, FF-11 #179, FF-12 #180), `/api/system/*`.
 *
 * Systemadminen er ikke et familiemedlem: egen cookie (`stell_site_sid`,
 * `Path=/api/system`), og ingenting her rører familieøkten eller
 * familiecachen. Passordet og koden holdes bare i skjemaets lokale state.
 * Alle skrivende kall under /api/system må være JSON (CSRF-vern, 415 ellers),
 * og de som endrer en familie krever fersk re-autentisering
 * (403 `reauth_required`, se useReauthGate).
 */
import type {
  SiteAdminLoginInput,
  SiteAdminLoginResponse,
  SiteAdminMe,
  SiteAdminReauthInput,
  SystemFamily,
  SystemFamilyAdminList,
  SystemFamilyCreateInput,
  SystemFamilyCreated,
  SystemFamilyLink,
  SystemFamilyList,
  SystemFamilyUpdateInput,
  SystemStats,
} from '@stell/shared';
import { jsonInit, sendJson } from '../../lib/api';

export const SYSTEM_LOGIN_PATH = '/system/logg-inn';
export const SYSTEM_HOME_PATH = '/system';
export const SITE_ADMIN_ME_KEY = ['system', 'me'] as const;
export const SYSTEM_FAMILIES_KEY = ['system', 'families'] as const;
export const SYSTEM_STATS_KEY = ['system', 'stats'] as const;

const FAMILIES = '/api/system/families';
const one = (id: string) => `${FAMILIES}/${encodeURIComponent(id)}`;

export const SystemApi = {
  login: (body: SiteAdminLoginInput) =>
    sendJson<SiteAdminLoginResponse>('/api/system/auth/login', jsonInit('POST', body)),
  me: () => sendJson<SiteAdminMe>('/api/system/auth/me'),
  logout: () => sendJson<{ ok: true }>('/api/system/auth/logout', jsonInit('POST', {})),
  reauth: (body: SiteAdminReauthInput) =>
    sendJson<{ ok: true; reauth_expires_at: string }>('/api/system/auth/reauth', jsonInit('POST', body)),

  families: () => sendJson<SystemFamilyList>(FAMILIES),
  createFamily: (body: SystemFamilyCreateInput) =>
    sendJson<SystemFamilyCreated>(FAMILIES, jsonInit('POST', body)),
  admins: (id: string) => sendJson<SystemFamilyAdminList>(`${one(id)}/admins`),
  adminLink: (id: string, memberId: string) =>
    sendJson<SystemFamilyLink>(`${one(id)}/admins/${encodeURIComponent(memberId)}/link`, jsonInit('POST', {})),
  updateFamily: (id: string, body: SystemFamilyUpdateInput) =>
    sendJson<SystemFamily>(one(id), jsonInit('PATCH', body)),
  suspend: (id: string) => sendJson<SystemFamily>(`${one(id)}/suspend`, jsonInit('POST', {})),
  reactivate: (id: string) => sendJson<SystemFamily>(`${one(id)}/reactivate`, jsonInit('POST', {})),
  deleteFamily: (id: string, code: string) => sendJson<{ ok: true }>(one(id), jsonInit('DELETE', { code })),

  /** FF-12 (#180): tall per familie for hele Oslo-dager `since`–`until`. */
  stats: (since: string, until: string) =>
    sendJson<SystemStats>(`/api/system/stats?${new URLSearchParams({ since, until }).toString()}`),
};
